//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetYouTubeConnectionStatusQuery } from '@account-settings/components/social-media-accounts/youtube/youtubeAccountSettingsQueries';
import {
  SocialNetworkUserInfo,
  SocialNetworksUserInfo,
} from '@account-settings/redux/accountSettings/accountSettingsModel';
import { getSocialNetworksUserInfo } from '@account-settings/redux/accountSettings/selectors/getSocialNetworksUserInfo';
import { getFacebookPageId } from '@account-settings/redux/facebook-instagram/selectors/getFacebookPageId';
import { getPinterestSettings } from '@account-settings/redux/pinterest/pinterestSettingsSlice';
import { getTikTokSettings } from '@account-settings/redux/tiktok/tiktokSettingsSlice';
import { getTwitterUserId } from '@account-settings/redux/twitter/selectors/getTwitterUserId';
import { facebookPageActions } from '@facebook/actions/facebookPageActions';
import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import { SocialNetwork } from '@models/SocialNetwork';
import { getPinterestUserId } from '@pinterest/redux/post-details/selectors/getPinterestUserId';
import { fetchUser as fetchPinterestUser } from '@pinterest/redux/user/pinterestUserSlice';
import { createThunk } from '@redux/createThunk';
import { getTikTokUserId } from '@tiktok/redux/user/selectors/getTikTokUser';
import { fetchUser as fetchTikTokUser } from '@tiktok/redux/user/tiktokUserSlice';
import { fetchSettingsAndUser as fetchTwitterUser } from '@twitterV2/redux/user/twitterUserSlice';
import { allScheduleNetworks } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { getIsYouTubeAutomaticSchedulingEnabled } from '@universal-scheduling/redux/scheduler/selectors/getIsYouTubeAutomaticSchedulingEnabled';
import { isDefined } from '@utils/filterPredicates';

/**
 * A hook that fetches and returns information about the user's account connection
 * for the given networks.
 */
export const useGetSocialNetworksUserInfo = (
  networks: SocialNetwork[] = allScheduleNetworks,
): SocialNetworksUserInfo => {
  const dispatch = useDispatch();

  // TODO: PD-264381: Cleanup youtube_automatic_posting feature flag
  const isYouTubeAutomaticSchedulingEnabled = useSelector(
    getIsYouTubeAutomaticSchedulingEnabled,
  );

  const socialNetworksUserInfo = useSelector(
    getSocialNetworksUserInfo(
      networks.filter(
        (network) =>
          network !== SocialNetwork.YouTube ||
          isYouTubeAutomaticSchedulingEnabled,
      ),
    ),
  );

  const [areAllFetched, setAreAllFetched] = useState<boolean>(
    socialNetworksUserInfo.every(({ isFetched }) => isFetched),
  );

  const fetchUser = async (network: SocialNetwork) => {
    switch (network) {
      case SocialNetwork.Facebook: {
        await dispatch(facebookPageActions.fetchPageTokenAndMetadata());
        break;
      }
      case SocialNetwork.Instagram: {
        await dispatch(instagramAccountActions.fetchDefaultInstagramAccount());
        break;
      }
      case SocialNetwork.Pinterest: {
        await Promise.all([
          dispatch(fetchPinterestUser()),
          dispatch(getPinterestSettings()),
        ]);
        break;
      }
      case SocialNetwork.TikTok: {
        await Promise.all([
          dispatch(fetchTikTokUser()),
          dispatch(getTikTokSettings()),
        ]);
        break;
      }
      case SocialNetwork.Twitter: {
        await dispatch(fetchTwitterUser());
        break;
      }
      case SocialNetwork.YouTube: {
        // this is handled using queries in getSocialNetworksUserInfo
        break;
      }
      default:
        console.error('Tried to request non-supported network');
    }
  };

  useEffect(() => {
    const fetchAllNetworkUsers = async () => {
      await Promise.all(
        socialNetworksUserInfo.map(
          async ({ isFetched, isFetching, network }) => {
            if (!isFetched && !isFetching) {
              await fetchUser(network);
            }
          },
        ),
      );
      setAreAllFetched(true);
    };

    void fetchAllNetworkUsers();
  }, []);

  const postingEntityIdsMap = dispatch(getPostingEntityIds(networks));

  return {
    areAllFetched,
    socialNetworksUserInfo: socialNetworksUserInfo.map((userInfo) => {
      const { isFetched, network, username } = userInfo;
      return {
        ...userInfo,
        isConnected: (areAllFetched || isFetched) && isDefined(username),
        isFetched,
        userId: PojoMap.get(postingEntityIdsMap, network),
      };
    }),
  };
};

export const useGetSocialNetworkUserInfo = (
  network: SocialNetwork,
): SocialNetworkUserInfo => {
  const { socialNetworksUserInfo } = useGetSocialNetworksUserInfo([network]);

  return (
    socialNetworksUserInfo[0] ?? {
      bio: undefined,
      followerCount: undefined,
      fullName: undefined,
      isConnected: false,
      isFetched: true,
      isFetching: false,
      isStale: false,
      network,
      profileImageUrl: undefined,
      username: '',
      userId: undefined,
    }
  );
};

export const getSocialNetworkUsernameDisplay = (
  socialNetwork: SocialNetwork,
  username?: string,
): string => {
  if (isDefined(username)) {
    return `${socialNetwork === SocialNetwork.Facebook ? '' : '@'}${username}`;
  }

  return '';
};

export const getPostingEntityIds = createThunk(
  (networks: SocialNetwork[]) => (dispatch, getState) => {
    // TODO: PD-264381: Cleanup youtube_automatic_posting feature flag
    const isYouTubeAutomaticSchedulingEnabled = useSelector(
      getIsYouTubeAutomaticSchedulingEnabled,
    );

    const facebookId = useSelector(getFacebookPageId)?.toString();

    const pinterestId = useSelector(getPinterestUserId);

    const tiktokId = useSelector(getTikTokUserId)?.toString();

    const twitterId = useSelector(getTwitterUserId)?.toString();

    const instagramId = getState().instagram.account.instagramBusinessAccount;

    const { data: youtubeUserInfo } = useGetYouTubeConnectionStatusQuery(
      isYouTubeAutomaticSchedulingEnabled,
    );
    const youtubeId = youtubeUserInfo?.userId;

    return networks.reduce((acc, network) => {
      switch (network) {
        case SocialNetwork.Facebook:
          if (isDefined(facebookId)) {
            return PojoMap.set(acc, network, facebookId);
          } else {
            return acc;
          }
        case SocialNetwork.Instagram:
          if (isDefined(instagramId)) {
            return PojoMap.set(acc, network, instagramId);
          } else {
            return acc;
          }
        case SocialNetwork.Pinterest:
          if (isDefined(pinterestId)) {
            return PojoMap.set(acc, network, pinterestId);
          } else {
            return acc;
          }
        case SocialNetwork.TikTok:
          if (isDefined(tiktokId)) {
            return PojoMap.set(acc, network, tiktokId);
          } else {
            return acc;
          }
        case SocialNetwork.Twitter:
          if (isDefined(twitterId)) {
            return PojoMap.set(acc, network, twitterId);
          } else {
            return acc;
          }
        case SocialNetwork.YouTube:
          if (isDefined(youtubeId)) {
            return PojoMap.set(acc, network, youtubeId);
          } else {
            return acc;
          }
        default:
          console.error('Tried to request non-supported network');
      }
      return acc;
    }, PojoMap.empty<SocialNetwork, string>());
  },
);
